import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import Select from 'react-select';

import { commafy } from "../../../utils/methods";
import { _post, _upload } from "../../../utils/network";

import "./FormField.style.scss";

const FormField = ({ type="input", fieldKey, title, tip=null, value, required=false, disabled=false, selectItems=null, fileMaxSize, multiSettings=null, isSearch=false, rtl=false, ltr=false, fullselectOnChange=null, selectOnChange=null, fullselectOnKeyUp=null, onChange=null, onUploadChange=null, onUploadLoadingChange=null, uploadContext=null, imageAspectRatio=null }) => {
	const { t } = useTranslation();

	const [file, setFiles] = useState([]);
	const [isUploading, setIsUploading] = useState(false);
	const uploadFileHandler =  async e => {
		let file = e.target.files[0];
		e.target.value = '';
		if(!file)
			return;
		if(file.size > (fileMaxSize * 1024 * 1024))
			return toast.warn(`${t('max_file_size')}: ${fileMaxSize} ${t('mb')}`);
		if(type === 'image' && !['image/avif', 'image/bmp', 'image/gif', 'image/jpeg', 'image/png', 'image/tiff', 'image/webp'].includes(file.type.toLowerCase()))
			return toast.warn('لطفاً یک فایل تصویر معتبر انتخاب کنید.');
		let data = new FormData();
		data.append("file", file);
		data.append("upload_file", true);
		if(uploadContext)
			data.append("upload_context", uploadContext);
		setIsUploading(true);
		onUploadLoadingChange?.(true);
		let response;
		try {
			response = await _upload("/upload", data);
		} catch {
			toast.error('آپلود تصویر انجام نشد. دوباره تلاش کنید.');
			return;
		} finally {
			setIsUploading(false);
			onUploadLoadingChange?.(false);
		}
		switch(response?.code){
			case 200:
				setFiles([response.data.image_address.replace('https', 'http')]);
				onUploadChange?.(response.data.image_address);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const directionClass = (ltr ? ' ltr' : '') + (rtl ? ' rtl' : '');

	const [multiItems, setMultiItems] = useState([]);
	const addMultiItem = button => {
		let selectValues = '';
		if(multiSettings.foreignKey)
				selectValues = multiSelectSelectedOption;
		let inputs = button.target.closest('.add-multi-item').querySelectorAll('input:not(.multi-select input)');
		let newMultiItem = (selectValues ? (selectValues + ',') : '') + Array.prototype.map.call(inputs, input => input.value).join(',');
		setMultiItems([...new Set([...multiItems, newMultiItem])]);
		Array.prototype.map.call(inputs, input => input.value = '').join(',');
	};
	const removeMultiItem = i => {
		let newMultiItems = [...multiItems];
		newMultiItems.splice(i, 1);
		setMultiItems(newMultiItems);
	};

	const [multiSelectTypedValue, setMultiSelectTypedValue] = useState(null);
	const multiSelectOnKeyUpHandler = () => {
		let typedValue = document.querySelector('.multi-foreign-select input').value.trim();
		setMultiSelectTypedValue(typedValue);
	};
	useEffect(() => {
		if(type === "multi" && multiSettings?.foreignKey)
			getMultiOptions();
	}, [multiSelectTypedValue]);
	const [multiSelectSelectedOption, setMultiSelectSelectedOption] = useState('');
	const multiSelectOnChangeHandler = event => {
		let selectedOption = event.label + ',' + event.value;
		setMultiSelectSelectedOption(selectedOption);
	};

	const [multiOptions, setMultiOptions] = useState([]);
	const getMultiOptions = async () => {
		let body = multiSettings?.foreignKey?.body;
		body[multiSettings?.foreignKey?.searchKey] = multiSelectTypedValue;
		let response = await _post(multiSettings?.foreignKey?.api, body);
		switch(response.code){
			case 200:
				setMultiOptions(response.data[multiSettings?.foreignKey?.response_key].map(item => {
					return {
						value: item[multiSettings?.foreignKey?.key],
						label: multiSettings?.foreignKey?.title.map(title => item[title]).join(' - ')
					};
				}));
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	useEffect(() => {
		if((type === "image" || type === "file" || type === "video") && !!value)
			setFiles([value]);
		if(type === "multi")
			setMultiItems(value ? JSON.parse(value) : []);
	}, [value]);

	return <>
		{(type === "input" || type === "password") ?
			<div className={(isSearch ? "col-sm-6 col-md-4" : "col-md-6") + " mb-3"}>
				<label className="form-label" htmlFor={fieldKey}>
					{title} {tip ? <small>({tip})</small> : ''}
					{required ? <span className="text-danger"> *</span> : ''}
				</label>
				<input className={"form-control" + directionClass} id={fieldKey} name={fieldKey} type={type === "password" ? "password" : "text"} defaultValue={value} placeholder={title} disabled={disabled} onChange={onChange || undefined} />
			</div>
		: type === "toggle" ?
			<div className={(isSearch ? "col-sm-6 col-md-4" : "col-md-3") + " mb-3"}>
				<label className="form-check-label" htmlFor={fieldKey}>
					{title} {tip ? <small>({tip})</small> : ''}
					{required ? <span className="text-danger"> *</span> : ''}
				</label>
				<div className="form-check form-switch">
					<input className="form-check-input" id={fieldKey} name={fieldKey} type="checkbox" defaultChecked={value} disabled={disabled} />
				</div>
			</div>
		: type === "select" ?
			<div className={(isSearch ? "col-sm-6 col-md-4" : "col-md-6") + " mb-3"}>
				<label className="form-check-label" htmlFor={fieldKey}>
					{title} {tip ? <small>({tip})</small> : ''}
					{required ? <span className="text-danger"> *</span> : ''}
				</label>
				<select className={"form-select" + directionClass} id={fieldKey} name={fieldKey} disabled={disabled} onChange={selectOnChange}>
					{required ? '' :
						<option value="">{isSearch ? t('all') : t('no_choice')}</option>
					}
					{selectItems.map(item =>
						<option value={item.value} key={item.value} selected={value === item.value} disabled={item.disabled}>{item.key}</option>
					)}
				</select>
			</div>
		: type === "fullselect" ?
			<div className={(isSearch ? "col-sm-6 col-md-4" : "col-md-6") + " mb-3"}>
				<label className="form-check-label" htmlFor={fieldKey}>
					{title} {tip ? <small>({tip})</small> : ''}
					{required ? <span className="text-danger"> *</span> : ''}
				</label>
				<div className="form-control" onKeyUp={fullselectOnKeyUp}>
					<Select className={directionClass} placeholder={title} options={selectItems} id={fieldKey} name={fieldKey} onChange={fullselectOnChange} />
				</div>
			</div>
		: type === "text" ?
			<div className={(isSearch ? "col-sm-6 col-md-4" : "col-md-6") + " mb-3"}>
				<label className="form-check-label">
					{title} {tip ? <small>({tip})</small> : ''}
					{required ? <span className="text-danger"> *</span> : ''}
				</label>
				<textarea className={"form-control" + directionClass} id={fieldKey} name={fieldKey} rows="4" placeholder={title} defaultValue={value ? value : ''} draggable={false} disabled={disabled}></textarea>
			</div>
		: type === "number" ?
			<div className={(isSearch ? "col-sm-6 col-md-4" : "col-md-6") + " mb-3"}>
				<label className="form-label" htmlFor={fieldKey}>
					{title} {tip ? <small>({tip})</small> : ''}
					{required ? <span className="text-danger"> *</span> : ''}
				</label>
				<input className={"form-control" + directionClass} id={fieldKey} name={fieldKey} type="text" defaultValue={value} placeholder={title} disabled={disabled} onChange={e => commafy(e, 'input')} />
			</div>
		: (type === "image" || type === "file" || type === "video") ?
			<div className={(isSearch ? "col-sm-6 col-md-4" : "col-md-6") + " mb-3"}>
				<label className="form-label" htmlFor={fieldKey + '-upload-input'}>
					{title} <small>({t('max_file_size')}: {fileMaxSize} {t('mb')}{tip ? ` - ${tip}` : ''})</small>
					{required ? <span className="text-danger"> *</span> : ''}
				</label>
				<div className={"upload-container mt-1" + (type === 'image' && imageAspectRatio ? ' upload-container--aspect' : '')} style={type === 'image' && imageAspectRatio ? { '--upload-image-aspect': imageAspectRatio } : undefined}>
					<div className="upload-box">
						<label htmlFor={fieldKey + '-upload-input'} title={isUploading ? t('uploading') : undefined}>
							<i className={isUploading ? "fas fa-spinner fa-spin" : "fas fa-upload"}></i>
							<input
								className="d-none"
								type="file"
								name={fieldKey + '-upload-input'}
								id={fieldKey + '-upload-input'}
								aria-label={title}
								accept={type === 'image' ? 'image/avif,image/bmp,image/gif,image/jpeg,image/png,image/tiff,image/webp' : type === 'video' ? 'video/*' : ''}
								onChange={e => uploadFileHandler(e)}
								disabled={disabled || isUploading}
							/>
						</label>
					</div>
					<div id={fieldKey + '-upload-list'} className="upload-list">
						{file.map((link, i) => type === "video" ?
							<div className="upload-image-preview" key={link}>
								<video src={link} data-upload-value={link} controls preload="metadata" className="upload-video-preview" />
								{onUploadChange && <button
									className="upload-image-remove"
									type="button"
									aria-label={`${t('delete')} ${title}`}
									title={t('delete')}
									disabled={disabled || isUploading}
									onClick={() => { setFiles([]); onUploadChange(null); }}
								>
									<i className="fa fa-times"></i>
								</button>}
							</div>
							: type === "image" && onUploadChange ?
							<div className="upload-image-preview" key={link}>
								<Link to={link} target="_blank" download={true}>
									<img src={link} alt={title} />
								</Link>
								<button
									className="upload-image-remove"
									type="button"
									aria-label={`${t('delete')} ${title}`}
									title={t('delete')}
									disabled={disabled || isUploading}
									onClick={() => { setFiles([]); onUploadChange(null); }}
								>
									<i className="fa fa-times"></i>
								</button>
							</div>
							:
							<Link to={link} key={link} target="_blank" download={true}>
								{type === "image" ?
									<img src={link} alt={title} />
								:
									<div className="file-container">
										<span>{t('download_file')}</span>
									</div>
								}
							</Link>
						)}
					</div>
				</div>
			</div>
		: type === "multi" ?
			<div className="col-12 mb-3">
				<label className="form-label" htmlFor={multiSettings?.keys[0]}>
					{title} {tip ? <small>({tip})</small> : ''}
					{required ? <span className="text-danger"> *</span> : ''}
				</label>
				<div className="input-group mb-1 add-multi-item">
					<button className="btn btn-success" type="button" onClick={e => addMultiItem(e)}>
						<i className="fa fa-plus"></i>
					</button>
					{multiSettings?.titles.map((title, i) => {
						if(multiSettings?.foreignKey && i==0)
							return <div className="form-control multi-select" onKeyUp={multiSelectOnKeyUpHandler}>
								<Select className={"multi-foreign-select " + multiSettings?.directions[i]} placeholder={multiSettings?.foreignKey?.searchTitle} options={multiOptions} key={multiSettings?.foreignKey?.searchTitle} onChange={e => multiSelectOnChangeHandler(e)} />
							</div>
						else if(multiSettings?.foreignKey && i==1)
							return '';
						return <input type="text" className={"form-control " + multiSettings?.directions[i] + directionClass} placeholder={title} id={multiSettings?.keys[i]} name={multiSettings?.keys[i]} key={title} />;
					})}
				</div>
				{multiItems?.map((item, i) =>
					<div className="input-group mb-1" key={item} data-form-field-multi-item={multiSettings?.keys[0]}>
						<button className="btn btn-danger" type="button" onClick={() => removeMultiItem(i)}>
							<i className="fa fa-minus"></i>
						</button>
						{multiSettings?.titles.map((title, j) =>
							<input type="text" className={"form-control " + multiSettings?.directions[j] + directionClass + (multiSettings?.foreignKey && j==1 ? ' d-none' : '')} placeholder={title} defaultValue={item.split(',')[j]} data-form-field-multi-item-input={multiSettings?.keys[j]} key={title} disabled />
						)}
					</div>
				)}
			</div>
		: ''}
	</>
};

export default FormField;
